import type { Flashcard, FlashcardCollection, FlashcardDeck } from "@japa-reader/contracts";
import { searchFlashcards } from "./flashcard-search";

export const DEFAULT_LEARN_AHEAD_MINUTES = 20;

export type FlashcardDeckCounts = {
  new: number;
  learning: number;
  review: number;
  total: number;
};

export function buildStudyQueue(collection: FlashcardCollection, deck: FlashcardDeck, query = "", now = Date.now()) {
  if (deck.kind === "filtered") {
    const filtered = activeCards(searchFlashcards(collection, query.trim() || deck.filter.trim() || "is:due", now), now);
    return sortReviews(filtered, deck.filterOrder).slice(0, Math.max(1, deck.filterLimit));
  }

  const deckIds = descendantDeckIds(deck.id, collection.decks);
  const custom = Boolean(query.trim());
  const searched = custom ? searchFlashcards(collection, query, now) : collection.cards;
  const candidates = activeCards(searched.filter((card) => deckIds.has(card.deckId)), now);
  const learning = candidates
    .filter((card) => ["learning", "relearning"].includes(card.state) && (custom || card.due <= learnAheadCutoff(now)))
    .sort((left, right) => left.due - right.due);
  const learningDue = custom ? learning : learning.filter((card) => card.due <= now);
  const learningAhead = custom ? [] : learning.filter((card) => card.due > now);
  let reviews = candidates.filter((card) => card.state === "review" && (custom || card.due <= now));
  let fresh = candidates.filter((card) => card.state === "new");
  reviews = sortReviews(reviews, deck.options.reviewOrder).slice(0, custom ? undefined : deck.options.maximumReviewsPerDay);
  fresh = sortNew(fresh, deck.options.newOrder).slice(0, custom ? undefined : deck.options.newCardsPerDay);
  if (deck.options.newReviewMix === "new-first") return [...learningDue, ...fresh, ...reviews, ...learningAhead];
  if (deck.options.newReviewMix === "review-first") return [...learningDue, ...reviews, ...fresh, ...learningAhead];
  return [...learningDue, ...interleave(reviews, fresh), ...learningAhead];
}

export function deckCounts(collection: FlashcardCollection, deckId: string, now = Date.now()): FlashcardDeckCounts {
  const deck = collection.decks.find((candidate) => candidate.id === deckId);
  if (!deck) return { new: 0, learning: 0, review: 0, total: 0 };
  if (deck.kind === "filtered") return countsFromCards(buildStudyQueue(collection, deck, "", now));

  const ids = descendantDeckIds(deckId, collection.decks);
  const cards = activeCards(collection.cards.filter((card) => ids.has(card.deckId)), now);
  const fresh = cards.filter((card) => card.state === "new").length;
  const learning = cards.filter((card) => ["learning", "relearning"].includes(card.state) && card.due <= learnAheadCutoff(now)).length;
  const review = cards.filter((card) => card.state === "review" && card.due <= now).length;
  const limitedNew = Math.min(fresh, deck.options.newCardsPerDay);
  const limitedReview = Math.min(review, deck.options.maximumReviewsPerDay);
  return { new: limitedNew, learning, review: limitedReview, total: limitedNew + learning + limitedReview };
}

function countsFromCards(cards: Flashcard[]): FlashcardDeckCounts {
  const counts = {
    new: cards.filter((card) => card.state === "new").length,
    learning: cards.filter((card) => card.state === "learning" || card.state === "relearning").length,
    review: cards.filter((card) => card.state === "review").length,
  };
  return { ...counts, total: counts.new + counts.learning + counts.review };
}

function activeCards(cards: Flashcard[], now: number) {
  return cards.filter((card) => !card.suspended && (!card.buriedUntil || card.buriedUntil <= now));
}

export function shouldRepeatLearningCardInSession(card: Flashcard, now = Date.now(), learnAheadMinutes = DEFAULT_LEARN_AHEAD_MINUTES) {
  return !card.suspended
    && (card.state === "learning" || card.state === "relearning")
    && card.due <= learnAheadCutoff(now, learnAheadMinutes);
}

function learnAheadCutoff(now: number, minutes = DEFAULT_LEARN_AHEAD_MINUTES) {
  return now + Math.max(0, minutes) * 60_000;
}

export function descendantDeckIds(rootId: string, decks: FlashcardDeck[]) {
  const ids = new Set([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const deck of decks) {
      if (deck.parentId && ids.has(deck.parentId) && !ids.has(deck.id)) {
        ids.add(deck.id);
        changed = true;
      }
    }
  }
  return ids;
}

function sortReviews(cards: Flashcard[], order: FlashcardDeck["options"]["reviewOrder"]) {
  const next = [...cards];
  if (order === "random") return shuffle(next);
  if (order === "interval-ascending") return next.sort((left, right) => left.scheduledDays - right.scheduledDays);
  if (order === "interval-descending") return next.sort((left, right) => right.scheduledDays - left.scheduledDays);
  return next.sort((left, right) => left.due - right.due);
}

function sortNew(cards: Flashcard[], order: FlashcardDeck["options"]["newOrder"]) {
  if (order === "random") return shuffle([...cards]);
  return [...cards].sort((left, right) => order === "reverse-added" ? right.position - left.position : left.position - right.position);
}

function shuffle<T>(values: T[]) {
  for (let index = values.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(Math.random() * (index + 1));
    [values[index], values[swap]] = [values[swap], values[index]];
  }
  return values;
}

function interleave<T>(left: T[], right: T[]) {
  const result: T[] = [];
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    if (left[index]) result.push(left[index]);
    if (right[index]) result.push(right[index]);
  }
  return result;
}
